import React, { useState, useEffect } from 'react';
import { Modal, Spin, Checkbox, Button, Alert, InputNumber, Divider, Tag } from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { apiGetTransactionDetail } from 'services/dashboardService';
import './RefundModal.css';

const RefundModal = ({ visible, onClose, onRefund, transaction, registrationName, loading, error }) => {
  const userDataStore = useSelector((x) => x.userDataStore);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [refundAmount, setRefundAmount] = useState(0);
  const [amountOverridden, setAmountOverridden] = useState(false);

  const originalAmount = parseFloat(transaction?.total_checkout || 0);

  useEffect(() => {
    if (visible && transaction?.transaction_id) {
      fetchDetail();
    }
    if (!visible) reset();
  }, [visible, transaction?.transaction_id]);

  const fetchDetail = async () => {
    setDetailLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('token', userDataStore.token);
      params.append('transaction_id', transaction.transaction_id);
      const res = await apiGetTransactionDetail(params);
      const d = res?.data ?? res;
      if (d && !Array.isArray(d.participants)) d.participants = [];
      setDetail(d);
      // Default: select everything (full refund)
      const allProgIds = (d?.participants || []).flatMap(p => (p.programs || []).map(prog => prog.id));
      setSelectedProgramIds(allProgIds);
      setRefundAmount(originalAmount);
    } catch (e) {
      console.error('RefundModal: failed to load detail', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const reset = () => {
    setDetail(null);
    setSelectedProgramIds([]);
    setRefundAmount(0);
    setAmountOverridden(false);
  };

  // Derived values
  const allParticipants = detail?.participants || [];
  const allProgramIds = allParticipants.flatMap(p => (p.programs || []).map(prog => prog.id));
  const isFullRefund = allProgramIds.length > 0 && allProgramIds.every(id => selectedProgramIds.includes(id));
  const hasAnySelection = selectedProgramIds.length > 0;

  const getParticipantState = (participant) => {
    const progIds = (participant.programs || []).map(p => p.id);
    if (progIds.length === 0) return 'none';
    const count = progIds.filter(id => selectedProgramIds.includes(id)).length;
    if (count === 0) return 'none';
    if (count === progIds.length) return 'all';
    return 'some';
  };

  const calcSuggestedAmount = (progIds) => {
    if (!detail) return 0;
    if (allProgramIds.length > 0 && allProgramIds.every(id => progIds.includes(id))) return originalAmount;
    let total = 0;
    for (const p of allParticipants) {
      for (const prog of (p.programs || [])) {
        if (progIds.includes(prog.id)) {
          total += parseFloat(prog.price_at_purchase || 0);
        }
      }
    }
    return parseFloat(total.toFixed(2));
  };

  // Auto-update amount when selection changes (unless admin manually typed an amount)
  useEffect(() => {
    if (!detail || amountOverridden) return;
    setRefundAmount(calcSuggestedAmount(selectedProgramIds));
  }, [selectedProgramIds, detail]);

  const handleSelectAll = (checked) => {
    setAmountOverridden(false);
    setSelectedProgramIds(checked ? [...allProgramIds] : []);
  };

  const handleParticipantToggle = (participant, checked) => {
    const progIds = (participant.programs || []).map(p => p.id);
    setAmountOverridden(false);
    setSelectedProgramIds(prev => {
      const without = prev.filter(id => !progIds.includes(id));
      return checked ? [...without, ...progIds] : without;
    });
  };

  const handleProgramToggle = (progId, checked) => {
    setAmountOverridden(false);
    setSelectedProgramIds(prev =>
      checked ? [...prev, progId] : prev.filter(id => id !== progId)
    );
  };

  const handleAmountChange = (val) => {
    setRefundAmount(val);
    setAmountOverridden(true);
  };

  const handleSubmit = () => {
    if (!hasAnySelection || !refundAmount || refundAmount <= 0) return;

    const participantIds = [];
    const programIds = [];

    for (const p of allParticipants) {
      const progIds = (p.programs || []).map(prog => prog.id);
      const allSelected = progIds.length > 0 && progIds.every(id => selectedProgramIds.includes(id));
      if (allSelected) {
        participantIds.push(p.id);
      } else {
        progIds.filter(id => selectedProgramIds.includes(id)).forEach(id => programIds.push(id));
      }
    }

    onRefund({
      authorization_amount: refundAmount,
      participant_ids: participantIds,
      program_ids: programIds,
      is_full_refund: isFullRefund,
    });
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      title={<span><DollarOutlined style={{ marginRight: 8, color: '#ff7a00' }} />Process Refund</span>}
      open={visible}
      onCancel={handleCancel}
      width={620}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={loading}>Cancel</Button>,
        <Button
          key="submit"
          type="primary"
          danger
          onClick={handleSubmit}
          loading={loading}
          disabled={!hasAnySelection || !refundAmount || refundAmount <= 0}
        >
          Process Refund
        </Button>,
      ]}
    >
      {error && (
        <Alert message="Refund Failed" description={error} type="error" showIcon style={{ marginBottom: 16 }} />
      )}

      <div className="refund-transaction-info">
        <p><strong>Registration:</strong> {registrationName || 'N/A'}</p>
        <p><strong>Customer:</strong> {[transaction?.parent_first, transaction?.parent_last].filter(Boolean).join(' ') || 'N/A'}</p>
        <p><strong>Original Amount:</strong> ${originalAmount.toFixed(2)}</p>
        <p><strong>Transaction ID:</strong> {transaction?.payment_txn_id || 'N/A'}</p>
      </div>

      {detailLoading ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}><Spin /></div>
      ) : detail ? (
        <>
          <Divider orientation="left" style={{ marginTop: 0 }}>Select Items to Cancel</Divider>

          <div style={{ marginBottom: 12 }}>
            <Checkbox
              checked={isFullRefund}
              indeterminate={hasAnySelection && !isFullRefund}
              onChange={e => handleSelectAll(e.target.checked)}
            >
              <strong>Select All (Full Refund)</strong>
            </Checkbox>
          </div>

          {allParticipants.length === 0 && (
            <p style={{ color: '#888', marginLeft: 8 }}>No participants found for this transaction.</p>
          )}

          {allParticipants.map(participant => {
            const state = getParticipantState(participant);
            return (
              <div
                key={participant.id}
                style={{ marginBottom: 16, paddingLeft: 12, borderLeft: '3px solid #e8e8e8' }}
              >
                <Checkbox
                  checked={state === 'all'}
                  indeterminate={state === 'some'}
                  onChange={e => handleParticipantToggle(participant, e.target.checked)}
                >
                  <strong>{participant.camper_first_name} {participant.camper_last_name}</strong>
                  {parseFloat(participant.camper_subtotal || 0) > 0 && (
                    <span style={{ marginLeft: 8, color: '#666', fontWeight: 'normal' }}>
                      ${parseFloat(participant.camper_subtotal).toFixed(2)}
                    </span>
                  )}
                </Checkbox>

                {(participant.programs || []).length > 0 && (
                  <div style={{ marginLeft: 24, marginTop: 6 }}>
                    {participant.programs.map(prog => (
                      <div key={prog.id} style={{ marginBottom: 4 }}>
                        <Checkbox
                          checked={selectedProgramIds.includes(prog.id)}
                          onChange={e => handleProgramToggle(prog.id, e.target.checked)}
                        >
                          <span>{prog.program_name}</span>
                          <span style={{ marginLeft: 8, color: '#888' }}>
                            ${parseFloat(prog.price_at_purchase || 0).toFixed(2)}
                          </span>
                          <Tag color={prog.program_type === 'main' ? 'blue' : 'purple'} style={{ marginLeft: 6, fontSize: 11 }}>
                            {prog.program_type === 'main' ? 'Registration' : 'Add-on'}
                          </Tag>
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <Divider />

          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 6 }}>
              <strong>Refund Amount</strong>
              {!isFullRefund && hasAnySelection && (
                <span style={{ marginLeft: 8, fontSize: 12, color: '#888' }}>
                  Processing fee excluded from partial calculation — adjust if needed
                </span>
              )}
            </div>
            <InputNumber
              prefix="$"
              value={refundAmount}
              onChange={handleAmountChange}
              precision={2}
              step={0.01}
              min={0.01}
              max={originalAmount}
              style={{ width: '100%' }}
            />
          </div>

          <Alert
            message="This action cannot be undone. The refund will be processed to the customer's original payment method and the selected items will be cancelled."
            type="warning"
            showIcon
          />
        </>
      ) : (
        <p style={{ color: '#888' }}>Unable to load transaction details.</p>
      )}
    </Modal>
  );
};

export default RefundModal;
